// ============================================================
// ThemeContext.jsx
// Provides dark/light theme switching across the entire app.
//
// HOW IT WORKS:
//   1. Reads saved preference from localStorage ('theme-mode')
//   2. Exposes the active theme object + a toggle function
//   3. Any component can call useTheme() to get colors & toggle
//
// USAGE:
//   import { useTheme } from '../context/ThemeContext';
//   const { colors, mode, toggleTheme } = useTheme();
//   <div style={{ background: colors.bg }}>...</div>
// ============================================================

import { createContext, useContext, useState, useEffect } from 'react';
import { lightTheme, darkTheme } from '../styles/theme';

// The key used to persist the user's choice in localStorage
const STORAGE_KEY = 'theme-mode';

// Create the context with a sensible default (light)
const ThemeContext = createContext({
  colors: lightTheme,
  mode: 'light',
  toggleTheme: () => {},
});

const applyThemeVariables = (colors, mode) => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const entries = {
    '--app-bg': colors.bg,
    '--app-surface-0': colors.surface0,
    '--app-card-bg': colors.cardBg,
    '--app-surface-2': colors.surface2,
    '--app-border': colors.border,
    '--app-border-sub': colors.borderSub,
    '--app-accent': colors.accent,
    '--app-accent-light': colors.accentLight,
    '--app-accent-dim': colors.accentDim,
    '--app-accent-glow': colors.accentGlow,
    '--app-accent-start': colors.accentStart,
    '--app-accent-end': colors.accentEnd,
    '--app-positive': colors.positive,
    '--app-negative': colors.negative,
    '--app-warning': colors.warning,
    '--app-gold': colors.gold,
    '--app-positive-dim': colors.positiveDim,
    '--app-negative-dim': colors.negativeDim,
    '--app-steel-dim': colors.steelDim,
    '--app-gold-dim': colors.goldDim,
    '--app-chart-blue': colors.chartBlue,
    '--app-chart-purple': colors.chartPurple,
    '--app-chart-navy': colors.chartNavy,
    '--app-text-main': colors.textMain,
    '--app-text-dim': colors.textDim,
    '--app-text-faint': colors.textFaint,
    '--app-badge-sto': colors.badgeSTO,
    '--app-badge-btc': colors.badgeBTC,
    '--app-badge-buy': colors.badgeBuy,
    '--app-badge-sell': colors.badgeSell,
    '--app-badge-roll': colors.badgeRoll,
    '--app-badge-dcf': colors.badgeDCF,
    '--app-badge-div': colors.badgeDIV,
    '--app-shadow-card': colors.shadow.card,
    '--app-shadow-elevated': colors.shadow.elevated,
    '--app-shadow-dropdown': colors.shadow.dropdown,
    '--app-glow-neutral': colors.glow.neutral,
    '--app-glow-green': colors.glow.green,
    '--app-glow-red': colors.glow.red,
    '--app-glow-purple': colors.glow.purple,
    '--font-main': '"C059", "Times New Roman", serif',
    '--font-display': '"C059", "Times New Roman", serif',
    '--font-mono': '"C059", "Times New Roman", serif',
  };

  Object.entries(entries).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  root.dataset.theme = mode;
  root.style.colorScheme = mode;
};

// ── Provider ────────────────────────────────────────────────
// Wrap your <App /> with this so every child can access theme.
export const ThemeProvider = ({ children }) => {
  // Read saved preference, default to 'light'
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'light';
    } catch {
      return 'light';
    }
  });

  // Pick the matching theme object
  const colors = mode === 'dark' ? darkTheme : lightTheme;

  // Persist changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage unavailable — silently ignore
    }
  }, [mode]);

  useEffect(() => {
    applyThemeVariables(colors, mode);
  }, [colors, mode]);

  // Toggle between light and dark
  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ colors, mode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ── Hook ────────────────────────────────────────────────────
// Shortcut so components don't need to import both
// useContext and ThemeContext separately.
export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
